import Vue from 'vue';
import Router, {NavigationGuardNext, Route} from 'vue-router';
import { UserTypes } from '@/models/User/UserTypes';
import { SubscriptionStatus } from '@/models/Subscriptions/SubscriptionStatus';
import UserDetails from './models/User/UserDetails';
import store from './store/store';
import { AddOnType } from './models/Subscriptions/AddOns/AddOnType';

Vue.use(Router);

const router = new Router({
  routes: [
    {
      path: '/',
      component: () => import('./views/User/User.vue'),
      props: true,
      children: [
        {
          path: '',
          name: 'dashboard',
          component: () => import('./views/User/Home.vue'),
        },
        {
          path: '/profile',
          name: 'profile',
          component: () => import('./views/User/Profile.vue'),
          meta: { breadCrumbs: ['dashboard', 'profile'] }
        },
        {
          path: '/manageLimits',
          name: 'manageLimits',
          component: () => import('./views/User/ManageLimits.vue'),
          meta: { breadCrumbs: ['dashboard', 'profile'] }
        },
        {
          path: '/subscription',
          name: 'subscription',
          component: () => import('./views/User/ManageSubscription.vue'),
          meta: {
            authorizationRoles: [UserTypes.Owner],
            breadCrumbs: ['dashboard', 'subscription']
          }
        },
        {
          path: '/payment',
          name: 'payment',
          component: () => import('./views/User/ManagePayment.vue'),
          meta: {
            authorizationRoles: [UserTypes.Owner],
            breadCrumbs: ['dashboard', 'payment']
          }
        },
        {
          path: '/subscription/pay/:id/:subscriptionCycle?',
          name: 'subscriptionPay',
          component: () => import('./views/User/ManageSubscription.vue'),
          meta: {
            authorizationRoles: [UserTypes.Owner],
            breadCrumbs: ['dashboard', 'subscription']
          }
        },
        {
          path: '/subscription/success',
          name: 'subscriptionSuccess',
          component: () => import('./views/User/ManageSubscription.vue'),
          meta: {
            authorizationRoles: [UserTypes.Owner],
            breadCrumbs: ['dashboard', 'subscription']
          }
        },
        {
          path: '/ingredients',
          name: 'ingredients',
          component: () => import('./views/User/Ingredients.vue'),
          meta: { breadCrumbs: ['dashboard', 'ingredients'] }
        },
        {
          path: '/ingredient-cost',
          name: 'ingredientCost',
          component: () => import('./views/User/IngredientCost.vue'),
          meta: { breadCrumbs: ['dashboard', 'ingr-cost-title'] }
        },
        {
          path: '/additional-cost',
          name: 'additionalCost',
          component: () => import('./views/User/AdditionalCost.vue'),
          meta: { breadCrumbs: ['dashboard', 'additional_cost'] }
        },
        {
          path: '/recipes',
          name: 'recipes',
          component: () => import('./views/User/Recipes.vue'),
          meta: { breadCrumbs: ['dashboard', 'recipes'] }
        },
        {
          path: '/recipes/add',
          name: 'recipesAdd',
          component: () => import('./views/User/RecipesManagement.vue'),
          meta: { breadCrumbs: ['dashboard', 'recipes', 'recipesAdd'] }
        },
        {
          path: '/recipes/:id',
          name: 'recipesEdit',
          component: () => import('./views/User/RecipesManagement.vue'),
          meta: { breadCrumbs: ['dashboard', 'recipes', 'recipeDetails'] }
        },
        {
          path: '/recipe-cost',
          name: 'recipesCost',
          component: () => import('./views/User/RecipesCost.vue'),
          meta: { breadCrumbs: ['dashboard', 'recipe-cost'] },
          beforeEnter: (to, from, next) => {
            let userDetails: UserDetails = store.getters.getUserDetails;
            // Check if appropriate add on exists
            if (userDetails.addOns &&
              userDetails.addOns.map((x: any) => x.addOn).includes(AddOnType.RecipeCost)) {
              next();
            }
            else {
              next({ name: "dashboard" });
            }
          }
        },
        {
          path: '/recipes/cost/:id',
          name: 'calculateRecipesCost',
          component: () => import('./views/User/CreateRecipeCost.vue'),
          meta: { breadCrumbs: ['dashboard', 'recipes', 'rec-cost-calculate-recipe-cost'] },
          beforeEnter: (to, from, next) => {
            let userDetails: UserDetails = store.getters.getUserDetails;
            // Check if appropriate add on exists
            if (userDetails.addOns &&
              userDetails.addOns.map((x: any) => x.addOn).includes(AddOnType.RecipeCost)) {
              next();
            }
            else {
              next({ name: "dashboard" });
            }
          }
        },
        {
          path: '/recipes/update-cost/:id',
          name: 'updateRecipeCost',
          component: () => import('./views/User/UpdateRecipeCost.vue'),
          meta: { breadCrumbs: ['dashboard', 'recipes', 'rec-cost-calculate-recipe-cost'] },
          beforeEnter: (to, from, next) => {
            let userDetails: UserDetails = store.getters.getUserDetails;
            // Check if appropriate add on exists
            if (userDetails.addOns &&
              userDetails.addOns.map((x: any) => x.addOn).includes(AddOnType.RecipeCost)) {
              next();
            }
            else {
              next({ name: "dashboard" });
            }
          }
        },
        {
          path: '/recipe-invoice',
          name: 'recipeInvoice',
          component: () => import('./views/User/RecipeInvoice.vue'),
          meta: { breadCrumbs: ['dashboard', 'recipe-invoice'] }
        },
        {
          path: '/recipe-invoice/add',
          name: 'recipeInvoiceAdd',
          component: () => import('./views/User/RecipeInvoiceManagement.vue'),
          meta: { breadCrumbs: ['dashboard', 'recipe-invoice', 'recipe-invoice-add'] }
        },
        {
          path: '/recipe-invoice/:id',
          name: 'recipeInvoiceEdit',
          component: () => import('./views/User/RecipeInvoiceManagement.vue'),
          meta: { breadCrumbs: ['dashboard', 'recipe-invoice', 'recipe-invoice-edit'] }
        },
        {
          path: '/dishes',
          name: 'dishes',
          component: () => import('./views/User/Dishes.vue'),
          meta: { breadCrumbs: ['dashboard', 'dishes'] }
        },
        {
          path: '/dishes/add',
          name: 'dishesAdd',
          component: () => import('./views/User/DishesManagement.vue'),
          meta: { breadCrumbs: ['dashboard', 'dishes', 'dishesAdd'] }
        },
        {
          path: '/dishes/:id',
          name: 'dishesEdit',
          component: () => import('./views/User/DishesManagement.vue'),
          meta: { breadCrumbs: ['dashboard', 'dishes', 'dishDetails'] }
        },
        {
          path: '/wellness',
          name: 'wellness',
          component: () => import('./views/User/wellness.vue'),
          meta: { breadCrumbs: ['dashboard', 'wellness'] },
          beforeEnter: (to, from, next) => {
            let userDetails: UserDetails = store.getters.getUserDetails;
            if (userDetails.addOns &&
              userDetails.addOns.map((x: any) => x.addOn).includes(AddOnType.WellnessModule)) {
              next();
            }
            else {
              next({ name: "dashboard" });
            }
          }
        },
        {
          path: '/allergens-ai',
          name: 'allergensAi',
          component: () => import('./views/User/AllergensAi.vue'),
          meta: { breadCrumbs: ['dashboard'] },
          beforeEnter: (to, from, next) => {
            let userDetails: UserDetails = store.getters.getUserDetails;
            if (userDetails.addOns &&
              userDetails.addOns.map((x: any) => x.addOn).includes(AddOnType.AllergenAI)) {
              next();
            }
            else {
              next({ name: "dashboard" });
            }
          }
        },
        {
          path: '/accountsettings',
          name: 'accountsettings',
          component: () => import('./views/User/AccountSettings.vue'),
          meta: { breadCrumbs: ['dashboard', 'account'] }
        },
        {
          path: '/training-videos',
          name: 'trainingVideos',
          component: () => import('@/views/User/TrainingVideos.vue'),
          meta: {
            breadCrumbs: ['dashboard', 'training_videos'],
            authorizationRoles: [UserTypes.Chef, UserTypes.Owner]
          }
        }
      ],
      meta: {
        requiresAuth: true,
        authorizationRoles: [UserTypes.Chef, UserTypes.Owner],
        redirectsTo: '/administration/login',
      },
    },
    {
      path: '/administration',
      component: () => import('./views/Admin/Admin.vue'),
      children: [
        {
          path: 'customers',
          name: 'customers',
          component: () => import('./views/Admin/Customers.vue'),
          meta: {
            authorizationRoles: [UserTypes.SuperAdministrator, UserTypes.Administrator, UserTypes.Helpdesk]
          }
        },
        {
          path: 'partners',
          name: 'partners',
          component: () => import('./views/Admin/Partners.vue'),
          meta: {
            authorizationRoles: [UserTypes.SuperAdministrator, UserTypes.Administrator]
          }
        },
        {
          path: 'packages',
          name: 'packages',
          component: () => import('./views/Admin/Packages.vue'),
          meta: {
            authorizationRoles: [UserTypes.SuperAdministrator, UserTypes.Helpdesk]
          }
        },
        {
          path: 'cancellation-workflow',
          name: 'cancellation-workflow',
          component: () => import('./views/Admin/CancellationWorkflow.vue'),
          meta: {
            authorizationRoles: [UserTypes.SuperAdministrator, UserTypes.Helpdesk]
          }
        },
        {
          path: 'retention-report',
          name: 'retention-report',
          component: () => import('./views/Admin/RetentionReport.vue'),
          meta: {
            authorizationRoles: [UserTypes.SuperAdministrator, UserTypes.Helpdesk]
          }
        },
        {
          path: 'coupons',
          name: 'coupons',
          component: () => import('./views/Admin/Coupons.vue'),
          meta: {
            authorizationRoles: [UserTypes.SuperAdministrator, UserTypes.Administrator, UserTypes.Helpdesk]
          }
        },
        {
          path: 'search_reports',
          name: 'search_reports',
          component: () => import('./views/Admin/SearchReports.vue'),
          meta: {
            authorizationRoles: [UserTypes.SuperAdministrator, UserTypes.Administrator, UserTypes.Helpdesk]
          }
        },
        {
          path: 'admins',
          name: 'admins',
          component: () => import('./views/Admin/AdminUsers.vue'),
          meta: {
            authorizationRoles: [UserTypes.SuperAdministrator]
          }
        },
        {
          path: 'configuration',
          name: 'configuration',
          component: () => import('./views/Admin/SystemConfiguration.vue'),
        },
        {
          path: 'account',
          name: 'account',
          component: () => import('./views/Admin/AccountSettings.vue'),
        },
        {
          path: 'calculations',
          name: 'calculations',
          component: () => import('./views/Admin/Calculations.vue'),
        },
        {
          path: 'allergens-ai',
          name: 'adminAllergensAi',
          component: () => import('./views/Admin/AllergensAi.vue'),
          meta: {
            authorizationRoles: [UserTypes.SuperAdministrator, UserTypes.Administrator, UserTypes.Helpdesk]
          },
        },
        {
          path: 'training-videos',
          name: 'training-videos',
          component: () => import('./views/Admin/TrainingVideos.vue'),
          meta: {
            authorizationRoles: [UserTypes.SuperAdministrator, UserTypes.Administrator, UserTypes.Helpdesk]
          },
        },
      ],
      meta: {
        requiresAuth: true,
        authorizationRoles: [UserTypes.SuperAdministrator, UserTypes.Administrator, UserTypes.Helpdesk,],
      },
    },
    {
      path: '/login',
      name: 'login',
      component: () => import('./views/User/Login.vue'),
      meta: { description: 'Access your MenuSano account to manage recipes, perform nutrition analysis, and generate compliant nutrition labels effortlessly.' },
    },
    {
      path: '/create',
      name: 'create',
      component: () => import('./views/User/DirectSignup.vue'),
    },
    {
      path: "/freetrial",
      name: "freetrial",
      component: () => import("./views/User/FreeTrialSignup.vue"),
    },
    {
      path: '/forgot',
      name: 'forgot',
      props: { forgotPassword: true },
      component: () => import('./views/User/Login.vue'),
      meta: { description: 'Reset your MenuSano account to manage recipes, perform nutrition analysis, and generate compliant nutrition labels effortlessly.' },
    },
    {
      path: '/resetpassword/:token',
      name: 'resetpassword',
      component: () => import('./views/User/ResetPassword.vue'),
    },
    {
      path: '/ingr',
      name: 'ingr',
      component: () => import('./components/vIngredientManagement.vue'),
    },
    {
      path: '*',
      redirect: { name: 'dashboard' },
    },
  ],
});

router.beforeEach((to, from, next) => {

  let userDetails: UserDetails = store.getters.getUserDetails;
  let isLogged: boolean = store.getters.isLoggedIn;

  // REMOVE MODAL OVERFLOW SETTING FROM BODY
  let body = document.getElementsByTagName("body")[0];
  body.classList.remove("modalOpen");

  if ((to.path.endsWith('login') || to.path.endsWith('create')) && isLogged) {
    if ([UserTypes.Administrator, UserTypes.SuperAdministrator, UserTypes.Helpdesk].includes(userDetails.userType)) {
      next('/administration/customers');
      return;
    }
    next('/');
    return;
  }

  if (to.path.endsWith('/administration')) {
    next('/administration/customers');
    return;
  }

  // Check if logged-in user AND the user type is in the restaurants roles list [owner, chef]
  if (!to.path.startsWith("/subscription") &&
    !to.path.startsWith("/payment") &&
    !to.path.endsWith('manageLimits') &&
    isLogged &&
    [UserTypes.Owner, UserTypes.Chef].includes(userDetails.userType) &&
    userDetails.isRecipeOrDishLimitExceeded &&
    userDetails.subscriptionStatus == SubscriptionStatus.Active
  ) {
    next("/manageLimits");
    return;
  }

  // Avoid navigating to pages that the user must be unauthenticated for
  handleUnauthOnlyPages(to, from, next);

  // Check authentication/authorization
  if (to.matched.some((record) => record.meta.requiresAuth)) {
    let r = to.matched.find((record) => record.meta.authorizationRoles);

    if (typeof r !== 'undefined') {

      if (isLogged && r.meta.authorizationRoles.includes(userDetails.userType)) {

        // check if child route is further filtered by role
        if (typeof to.meta.authorizationRoles !== 'undefined') {
          if (to.meta.authorizationRoles.includes(userDetails.userType)) {
            next();
            return;
          }

          else {
            next('/');
            return;
          }
        }

        next();
        return;
      }

      else {
        if (r.meta.authorizationRoles.includes([UserTypes.Administrator, UserTypes.SuperAdministrator, UserTypes.Helpdesk].includes(userDetails.userType))) {
          next('/administration/login');
          return;
        }
      }
    }

    next('/login');
    return;
  }

  // Update the page meta description
  if (to.meta.description) {
    document.head.querySelector('meta[name="description"]').setAttribute('content', to.meta.description);
  }

  next();
});

/**
 * Handles navigation to pages that are only accessible to unauthenticated users.
 * Redirects authenticated users away from login, create, or free trial pages.
 *
 * @param {Route} to - The target route that is being navigated to.
 * @param {Route} from - The current route that is being navigated away from.
 * @param {NavigationGuardNext<Vue<Record<string, any>, Record<string, any>, never, never, (event: string, ...args: any[]) => Vue>>} next - The navigation guard callback function to either proceed with or cancel the navigation.
 * @return {void} No return value; modifies the navigation flow based on authentication status.
 */
function handleUnauthOnlyPages(
  to: Route,
  from: Route,
  next: NavigationGuardNext<Vue<Record<string, any>, Record<string, any>, never, never, (event: string, ...args: any[]) => Vue>>
) {
  const isLogged: boolean = store.getters.isLoggedIn;

  if (isLogged) {
    if (to.path.endsWith('login') || to.path.endsWith('create') || to.path.endsWith('freetrial')) {
      next('/');
      return;
    }
  }
}
export default router;
